import React from 'react';
import {connect} from 'react-redux';
import {
  ISupplierUserProfile,
  IWalletStatement,
  IWalletTransaction,
} from 'constants/interfaces';
import {Box, Button, Snackbar, Typography} from '@material-ui/core';
import {KhulaSupplierTable} from 'components/Table/KhulaSupplierTable';
import {
  defaultWalletTableActiveFilters,
  walletTableFilters,
  walletTableHeaders,
  walletTableRows,
} from 'screens/Dashboard/Wallet/WalletTableSetup';
import moment, {Moment} from 'moment/moment';
import {_NumberWithSpaces} from 'constants/index';
import {WalletWithdrawDialog} from 'screens/Dashboard/Wallet/WalletWithdrawDialog';
import {
  apiCreateWallet,
  apiGetWalletStatement,
  apiUserHasWalletPinCode,
} from 'constants/Api';
import NavigationService from 'services/NavigationService';
import {UpdateWalletPinCodeDialog} from 'screens/Dashboard/Wallet/UpdateWalletPinCodeDialog';
import {Skeleton} from '@material-ui/lab';
import IconButton from '@material-ui/core/IconButton';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

type WalletProps = {
  user: ISupplierUserProfile;
  hideTitle?: boolean;
};

class Wallet extends React.Component<WalletProps> {
  state: {
    isLoading: boolean;
    isRefreshing: boolean;
    showRefreshedSnackbar: boolean;
    withdrawTransaction?: IWalletTransaction;
    showWithdrawSuccessSnackbar: boolean;
    statement?: IWalletStatement;
    startDate: Moment;
    endDate: Moment;
    hasPinCode?: boolean;
    showUpdatePinCodeDialog: boolean;
    showUpdatePinCodeSuccessSnackbar: boolean;
  } = {
    isLoading: true,
    isRefreshing: false,
    showRefreshedSnackbar: false,
    withdrawTransaction: undefined,
    showWithdrawSuccessSnackbar: false,
    statement: undefined,
    startDate: moment().startOf('month'),
    endDate: moment().endOf('month'),
    hasPinCode: undefined,
    showUpdatePinCodeDialog: false,
    showUpdatePinCodeSuccessSnackbar: false,
  };

  userCompanyDoesNotHaveBankInfo() {
    const company = this.props.user.company;
    return !(
      company?.bankId &&
      company.bankAccountNumber &&
      company.bankAccountTypeId &&
      company.bankBranchCode
    );
  }

  componentDidMount() {
    if (this.userCompanyDoesNotHaveBankInfo()) return;
    const {startDate, endDate} = this.state;
    this.setState({isLoading: true}, () => {
      Promise.resolve()
        .then(async () => {
          const hasWallet = this.props.user.company?.gpayEntityId;
          if (!hasWallet) {
            const createdWallet = await apiCreateWallet();
            if (!createdWallet.success) throw new Error(createdWallet.message);
          }
        })
        .then(() => this.getStatement(startDate, endDate))
        .then((statement) => {
          this.setState({statement, isLoading: false});
        })
        .then(async () => {
          const hasPinCode = await apiUserHasWalletPinCode(
            this.props.user.userId,
          );
          this.setState({hasPinCode: hasPinCode});
        })
        .catch((err) => {
          alert(
            'Failed to load statement at the moment. Please try again later',
          );
          console.error(err);
          this.setState({statement: undefined, isLoading: false});
        });
    });
  }

  refreshWallet(startDate: Moment | null, endDate: Moment | null) {
    if (this.userCompanyDoesNotHaveBankInfo()) return;
    this.setState({isRefreshing: true}, () => {
      this.getStatement(
        this.state.startDate || startDate,
        this.state.endDate || endDate,
      )
        .then((statement) => {
          this.setState({
            statement,
            isRefreshing: false,
            showRefreshedSnackbar: true,
          });
        })
        .catch((err) => {
          alert(
            'Failed to load statement at the moment. Please try again later',
          );
          console.error(err);
          this.setState({
            statement: undefined,
            isRefreshing: false,
          });
        });
    });
  }

  getStatement(startDate: Moment, endDate: Moment): Promise<IWalletStatement> {
    return apiGetWalletStatement(
      this.props.user,
      startDate.toDate(),
      endDate.toDate(),
    );
  }

  handleSetStartDate(startDate: Moment | null) {
    this.setState({startDate});
    this.refreshWallet(startDate, this.state.endDate);
  }

  handleSetEndDate(endDate: Moment | null) {
    this.setState({endDate});
    this.refreshWallet(this.state.startDate, endDate);
  }

  handleDecrementMonth() {
    const newStartDate = this.state.startDate
      .subtract(1, 'month')
      .startOf('month');
    const newEndDate = this.state.endDate.subtract(1, 'month').endOf('month');
    this.setState({startDate: newStartDate, endDate: newEndDate});
    this.refreshWallet(newStartDate, newEndDate);
  }

  handleIncrementMonth() {
    const newStartDate = this.state.startDate.add(1, 'month').startOf('month');
    const newEndDate = this.state.endDate.add(1, 'month').endOf('month');
    this.setState({startDate: newStartDate, endDate: newEndDate});
    this.refreshWallet(newStartDate, newEndDate);
  }

  isCurrMonth() {
    return moment().isBetween(this.state.startDate, this.state.endDate);
  }

  render() {
    const {
      statement,
      isLoading,
      isRefreshing,
      showRefreshedSnackbar,
      withdrawTransaction,
      showWithdrawSuccessSnackbar,
      startDate,
      endDate,
      hasPinCode,
      showUpdatePinCodeDialog,
      showUpdatePinCodeSuccessSnackbar,
    } = this.state;

    if (this.userCompanyDoesNotHaveBankInfo())
      return <WalletOnboarding hideTitle={this.props.hideTitle} />;

    return (
      <Box className="table-screen wallet-container">
        {!this.props.hideTitle && (
          <Typography variant="h5" className="page-title">
            Wallet
          </Typography>
        )}

        <Box className="balances-container">
          <div>
            {/* <Typography variant="body2">Opening Balance:</Typography>*/}
            <Typography variant="body2">Available Balance:</Typography>
          </div>
          <div>
            {/* <Typography variant="body2" align="right">*/}
            {/*  {statement ? _NumberWithSpaces(statement.AccountBalance) : ''}*/}
            {/* </Typography>*/}
            <Typography variant="body2" align="right">
              {statement
                ? 'R ' + _NumberWithSpaces(statement.AvailableBalance)
                : ''}
            </Typography>
          </div>
        </Box>

        <Box className="actions-row">
          <IconButton size="small" onClick={() => this.handleDecrementMonth()}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="body1">
            {startDate.format('MMMM YYYY')}
          </Typography>
          <IconButton
            size="small"
            onClick={() => this.handleIncrementMonth()}
            disabled={this.isCurrMonth()}
          >
            <ChevronRightIcon />
          </IconButton>

          <Box flex={1} />

          {hasPinCode === undefined && (
            <Skeleton width={80} height={40} variant="rect" />
          )}
          {hasPinCode !== undefined && (
            <Button
              variant="outlined"
              onClick={() => {
                this.setState({showUpdatePinCodeDialog: true});
              }}
              size="small"
              color="default"
            >
              {hasPinCode ? 'Update' : 'Set'} Pin
            </Button>
          )}
        </Box>

        <div className="orders-section">
          <KhulaSupplierTable
            headers={walletTableHeaders()}
            rows={walletTableRows(statement?.Statement || [], (transaction) =>
              this.setState({withdrawTransaction: transaction}),
            )}
            defaultSortField={'formattedDate'}
            defaultSortIsAscending={false}
            filtersGroups={walletTableFilters}
            defaultActiveFilters={defaultWalletTableActiveFilters}
            isLoading={isLoading}
            isRefreshing={isRefreshing}
            onRefresh={() => this.refreshWallet(startDate, endDate)}
            onRowSelect={(row) => {
              const purchaseOrder =
                statement?.Statement?.[row.id as number]?.inputsPurchaseOrder;
              if (!purchaseOrder) return;
              NavigationService.navigate(
                `/purchase-orders/${purchaseOrder.id}`,
              );
            }}
          />
          <Snackbar
            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            open={showRefreshedSnackbar}
            onClose={() => this.setState({showRefreshedSnackbar: false})}
            autoHideDuration={3000}
            message="Wallet refreshed"
          />
          <WalletWithdrawDialog
            user={this.props.user}
            transaction={withdrawTransaction}
            open={!!withdrawTransaction}
            onConfirm={() => {
              this.setState({
                withdrawTransaction: undefined,
                showWithdrawSuccessSnackbar: true,
              });
              this.refreshWallet(startDate, endDate);
            }}
            onCancel={() => {
              this.setState({withdrawTransaction: undefined});
            }}
          />
          <Snackbar
            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            open={showWithdrawSuccessSnackbar}
            onClose={() => this.setState({showWithdrawSuccessSnackbar: false})}
            autoHideDuration={3000}
            message="Withdrawal succesful"
          />
          <UpdateWalletPinCodeDialog
            user={this.props.user}
            open={showUpdatePinCodeDialog}
            onConfirm={() => {
              this.setState({
                showUpdatePinCodeDialog: false,
                showUpdatePinCodeSuccessSnackbar: true,
              });
            }}
            onCancel={() => {
              this.setState({showUpdatePinCodeDialog: false});
            }}
          />
          <Snackbar
            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            open={showUpdatePinCodeSuccessSnackbar}
            onClose={() =>
              this.setState({showUpdatePinCodeSuccessSnackbar: false})
            }
            autoHideDuration={3000}
            message="Pin code updated"
          />
        </div>
      </Box>
    );
  }
}

function WalletOnboarding(props: {hideTitle?: boolean}) {
  return (
    <Box className="table-screen wallet-container">
      {!props.hideTitle && (
        <Typography variant="h5" className="page-title">
          Wallet
        </Typography>
      )}
      <Box className="onboarding-container">
        <Typography variant="body2">
          Update your company bank details to start using wallet
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => NavigationService.navigate(`/company`)}
        >
          Update
        </Button>
      </Box>
    </Box>
  );
}

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
  };
};

export default connect(mapStateToProps)(Wallet);
